import type { RecorderConfig } from '../types';

export const formatFileSize = (bytes: number): string => {
  // Guard invalid / tiny values
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';

  const k = 1024;
  const units = ['B', 'KB', 'MB', 'GB', 'TB'] as const;
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), units.length - 1);

  const value = bytes / Math.pow(k, i);
  // Keep two decimals, no parseFloat re-rounding needed
  return `${value.toFixed(2)} ${units[i]}`;
};

export const formatDuration = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;

  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  if (mins > 0) {
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }
  return `${secs}s`;
};

export const formatBitrate = (bitsPerSecond: number): string => {
  if (!Number.isFinite(bitsPerSecond) || bitsPerSecond <= 0) return '0 kbps';

  const kbps = bitsPerSecond / 1000;
  if (kbps < 1000) {
    return `${Math.round(kbps)} kbps`;
  }
  const mbps = kbps / 1000;
  return `${mbps.toFixed(1)} Mbps`;
};

export const formatTimestamp = (timestamp: string | Date): string => {
  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  // Fallback for invalid dates
  if (!(date instanceof Date) || isNaN(date.getTime())) return String(timestamp);

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).format(date);
};

export const formatProgress = (current: number, total: number): string => {
  if (!Number.isFinite(current) || !Number.isFinite(total) || total <= 0) return '0%';
  const percentage = Math.max(0, Math.min(100, (current / total) * 100));
  return `${Math.round(percentage)}%`;
};

export const formatUploadSpeed = (bytesPerSecond: number): string => {
  if (!Number.isFinite(bytesPerSecond) || bytesPerSecond <= 0) return '0 B/s';
  return `${formatFileSize(bytesPerSecond)}/s`;
};

export const formatETA = (remainingBytes: number, bytesPerSecond: number): string => {
  if (!Number.isFinite(bytesPerSecond) || bytesPerSecond <= 0) return 'Calculating...';
  if (!Number.isFinite(remainingBytes) || remainingBytes <= 0) return '0s remaining';

  const remainingSeconds = remainingBytes / bytesPerSecond;

  if (remainingSeconds < 60) {
    return `${Math.round(remainingSeconds)}s remaining`;
  }

  if (remainingSeconds < 3600) {
    const minutes = Math.round(remainingSeconds / 60);
    return `${minutes}m remaining`;
  }

  const hours = Math.floor(remainingSeconds / 3600);
  const minutes = Math.round((remainingSeconds % 3600) / 60);
  return `${hours}h ${minutes}m remaining`;
};

export const truncateText = (text: string, maxLength: number): string => {
  if (maxLength <= 3 || text.length <= maxLength) return text;
  return `${text.substring(0, maxLength - 3)}...`;
};

// Overloads so you can pass either the strict union or any string.
// Unknown strings are returned unchanged.
export function formatQualityLabel(
    quality: NonNullable<RecorderConfig['videoQuality']>
): string;
export function formatQualityLabel(quality: string): string;
export function formatQualityLabel(quality: string): string {
  const map: Record<NonNullable<RecorderConfig['videoQuality']>, string> = {
    low: 'Low (640×480)',
    medium: 'Medium (1280×720)',
    high: 'High (1920×1080)',
    auto: 'Auto (Adaptive)'
  };

  return (map as Record<string, string>)[quality] ?? quality;
}
